import * as React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Header from "../components/common/header/Header";
import { Article } from "../interfaces/Article";
import Stack from "@mui/material/Stack";
import Footer from "../components/common/Footer/Footer";

import theme from "../theme";
import {
  Alert,
  Typography,
  Breadcrumbs,
  Container,
  Fab,
  Fade,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Virtuoso } from "react-virtuoso";
import { getTyres, searchTyresF5 } from "../services/core-api";
import LocalProductItem from "../components/products/LocalProductItem";
import { formatTireSizeLabel } from "../utils/tireSizeUtils";

const SIZE_SEARCH_LIMIT = 100;

const mapProductToArticle = (product: any): Article =>
  ({
    ...product,
    productId: product.id ?? product.productId,
    id: product.id ?? product.productId ?? product.articleNumber,
    description: product.description || product.articleNumber,
  } as Article);

const TireProductSearchPage = () => {
  const [allArticles, setAllArticles] = React.useState<Article[]>([]);
  const [fitmentSizes, setFitmentSizes] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [warning, setWarning] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [vehicleLabel, setVehicleLabel] = React.useState("");
  const [showScrollTop, setShowScrollTop] = React.useState(false);

  const isFirstRun = React.useRef(true);

  React.useEffect(() => {
    if (!isFirstRun.current) return;
    isFirstRun.current = false;

    const urlParams = new URLSearchParams(window.location.search);
    const width = urlParams.get("width");
    const height = urlParams.get("height");
    const dimension = urlParams.get("dimension");
    const fitment = urlParams.get("fitment");
    const make = urlParams.get("make");
    const model = urlParams.get("model");
    const year = urlParams.get("year");
    const modification = urlParams.get("modification");

    // Size mode from header search (e.g. 195/65R15) — local GOMA DB
    if (width && height && dimension) {
      const sizeLabel = formatTireSizeLabel({ width, height, dimension });
      setVehicleLabel(sizeLabel);
      setFitmentSizes([{ raw: sizeLabel, width, height, diameter: dimension }]);

      (async () => {
        try {
          const response = await getTyres({
            width,
            height,
            dimension,
            page_number: 1,
            limit: SIZE_SEARCH_LIMIT,
          });
          const tyres = (response?.tyres || []).map(mapProductToArticle);
          setAllArticles(tyres);
        } catch (err: any) {
          const status = err?.response?.status;
          if (status === 404) {
            setAllArticles([]);
          } else {
            const detail =
              err?.response?.data?.detail ||
              "Failed to load tires for this size.";
            setError(String(detail));
          }
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // Vehicle fitment → Financa5 ArtikujOData (no Wheel-size product mapping)
    if (fitment) {
      const vehicleBits = [make, model, year, modification].filter(Boolean);
      setVehicleLabel(
        vehicleBits.length ? `${vehicleBits.join(" / ")} · ${fitment}` : fitment
      );
      setFitmentSizes([{ raw: fitment }]);

      (async () => {
        try {
          const response = await searchTyresF5(fitment);
          const tyres = (response?.tyres || []).map(mapProductToArticle);
          setAllArticles(tyres);
          if (response?.warning) setWarning(String(response.warning));
        } catch (err: any) {
          const detail =
            err?.response?.data?.detail ||
            "Failed to load tires from Financa5.";
          setError(String(detail));
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    setError(
      "Missing filters. Search a tire size (e.g. 195/65R15) in the header, or use Tire search on the home page and select a fitment size."
    );
    setLoading(false);
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition =
        window.scrollY || document.documentElement.scrollTop;
      setShowScrollTop(scrollPosition > 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const renderProductItem = React.useCallback(
    (index: number) => {
      if (index >= allArticles.length) return null;
      const article = allArticles[index];
      return (
        <LocalProductItem
          key={`${
            (article as any).productId ||
            (article as any).id ||
            article.articleNumber
          }-${index}`}
          article={article}
        />
      );
    },
    [allArticles]
  );

  return (
    <>
      <Header />
      <Box
        sx={{
          flexGrow: 1,
          m: 2,
          minHeight: "71vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              <Box
                sx={{
                  bgcolor: "#f1f1f1",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  paddingLeft: 1,
                  borderTop: `2px solid ${theme.palette.primary.dark}`,
                }}
              >
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={8}
                    sx={{ paddingLeft: "0px !important" }}
                  >
                    <Stack
                      spacing={2}
                      sx={{ paddingTop: "3px", paddingLeft: "5px" }}
                    >
                      <Breadcrumbs aria-label="breadcrumb">
                        <Typography
                          key="3"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontSize: "15px",
                            fontFamily: "Inter",
                          }}
                        >
                          Goma {vehicleLabel ? `— ${vehicleLabel}` : ""}
                        </Typography>
                      </Breadcrumbs>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {error && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {warning && !error && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {warning}
                </Alert>
              )}

              {!error && !loading && fitmentSizes.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: theme.palette.text.primary }}
                >
                  Fitment size:{" "}
                  {fitmentSizes
                    .map((s) =>
                      s.raw ||
                      [s.width, s.height, s.diameter]
                        .filter(Boolean)
                        .join("/")
                    )
                    .join(", ")}
                  {allArticles.length > 0 && (
                    <>
                      {" · "}
                      {allArticles.length} tires
                    </>
                  )}
                </Typography>
              )}

              <Box sx={{ mt: 4 }}>
                {loading && (
                  <Typography variant="body2">Loading tires…</Typography>
                )}
                {!loading && !error && allArticles.length === 0 && (
                  <Alert severity="info">
                    No tire articles found in Financa5 for this fitment size.
                  </Alert>
                )}
                {!loading && !error && allArticles.length > 0 && (
                  <Virtuoso
                    useWindowScroll
                    style={{ height: "calc(100vh - 150px)", width: "100%" }}
                    totalCount={allArticles.length}
                    itemContent={renderProductItem}
                    overscan={600}
                    increaseViewportBy={{ top: 600, bottom: 600 }}
                    defaultItemHeight={200}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
      <Fade in={showScrollTop}>
        <Fab
          color="secondary"
          aria-label="scroll to top"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Fade>
    </>
  );
};

export default TireProductSearchPage;
